/* ============================================================
   ProductTour — TASK-145, issue #238 (owner request 2026-08-31)
   A thin, self-built "обучашка" (onboarding walkthrough): a handful of
   contextual tooltips that point at real chrome already on screen —
   Sidebar / Topbar controls mounted by AppShell on every /app/* route, plus
   the Dashboard's own greeting row. No third-party tour SDK (weight +
   privacy, per owner scope) — this is a small state machine + a positioned
   tooltip, styled to match the app (see styles/product-tour.css).

   Gated end-to-end behind productTourEnabled() ('dathent.tour.enabled',
   DARK default OFF, see lib/flags.js). While off this renders nothing.

   Trigger: automatically on first /app visit — i.e. on mount, once a user
   is known — UNLESS this browser already finished or skipped it
   ('dathent.tour.done.<userId>' in localStorage; written on both
   completion and skip, current step index is NOT persisted, MVP). Manual
   restart: dispatch a `window` CustomEvent named 'dathent:tour:restart'
   (no detail payload needed) — e.g. a future "?" topbar button would do
   `window.dispatchEvent(new Event('dathent:tour:restart'))`. That button is
   NOT part of this task (left for a separate Topbar change so as not to
   collide with other in-flight Topbar work) — this component only listens.

   Steps live in lib/tourSteps.js: {id, selector, titleKey, bodyKey, screen}.
   `screen` is metadata only — THIS ENGINE NEVER NAVIGATES the user between
   routes to "complete" a step. If a step's selector isn't present on the
   current page (wrong screen, flag-gated nav item hidden, element not yet
   mounted), it is skipped silently after one short retry — never a crash,
   never a tooltip pointing at nothing.

   Suppressed entirely (checked on a poll while a tour is in progress, so it
   reacts to conditions appearing/disappearing mid-tour, not just at mount):
     - the preview-paywall blur (TASK-143, `.preview-paywall` in the DOM)
     - the UpgradeModal (`.upm-scrim` in the DOM — only rendered while open,
       see UpgradeModal.jsx `if (!open) return null`)
     - the /onboarding route tree (defensive: this component is only ever
       mounted inside the /app route tree — see App.jsx — so this route
       check should never actually trigger, but it costs nothing to keep)
   None of these end the tour — they just hide it until the blocker clears,
   same run in progress resumes.

   z-index 950: under the preview-paywall blur (990) and the UpgradeModal
   (1000), above ordinary app content — mirrors OnboardingNudge's slot
   (z-index 900) one layer up so the two don't visually collide if both are
   DARK-enabled on the same browser (unlikely in practice — different pilot
   cohorts — but cheap to keep non-overlapping).

   Funnel events (lib/funnel.js — no-op while its own flag is off, and every
   call here is try/catch'd regardless): 'tour_started' (once per run, incl.
   a manual restart), 'tour_step' {id} (once per step actually shown),
   'tour_completed' (last step's "Done"), 'tour_skipped' {at_step} (whichever
   step index was on screen when Skip was pressed).

   Mounted once inside PreviewProvider (App.jsx), sibling of <OnboardingNudge/>.
   ============================================================ */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icons.jsx';
import { useAuth } from '../contexts/AuthContext';
import { productTourEnabled } from '../lib/flags.js';
import { isMobileViewport } from '../lib/viewport.js';
import { track } from '../lib/funnel.js';
import { TOUR_STEPS } from '../lib/tourSteps.js';

const DONE_KEY_PREFIX = 'dathent.tour.done';

function doneKey(userId) {
  return `${DONE_KEY_PREFIX}.${userId || 'anon'}`;
}

function readDone(userId) {
  try {
    return window.localStorage.getItem(doneKey(userId)) === '1';
  } catch (_) {
    return false;
  }
}

function writeDone(userId) {
  try {
    window.localStorage.setItem(doneKey(userId), '1');
  } catch (_) { /* storage unavailable — worst case the tour re-offers itself */ }
}

// Surfaces that must win over the tour — checked live, not just at mount, so
// the tour hides itself the instant one of these appears and can resume once
// it clears (see file header).
function isSuppressed() {
  try {
    if (document.querySelector('.preview-paywall')) return true;
    if (document.querySelector('.upm-scrim')) return true;
    if (typeof window !== 'undefined' && window.location
      && String(window.location.pathname || '').startsWith('/onboarding')) return true;
  } catch (_) {
    return false;
  }
  return false;
}

export default function ProductTour() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [resolvedIdx, setResolvedIdx] = useState(null);
  const [rect, setRect] = useState(null);

  // 1 = walking forward (auto-start, "Next"), -1 = walking backward ("Back").
  // Read by the resolution effect below when a step's selector is missing
  // and it has to decide which neighbour to try next.
  const dirRef = useRef(1);
  // Step ids we've already fired 'tour_step' for THIS run — a step can be
  // "current" across several poll ticks; this keeps the event to one shot.
  const trackedRef = useRef(new Set());

  // ---- Trigger: auto-start once per user (per doneKey), + manual restart ----
  useEffect(() => {
    if (!productTourEnabled()) return undefined;
    let cancelled = false;

    const start = () => {
      if (cancelled || !user) return;
      // Mobile shell fix: coach-marks cover the mobile UI (owner: "На
      // мобильной версии обучалку выключить нужно") — never auto-start.
      if (isMobileViewport()) return;
      if (readDone(user.id)) return;
      dirRef.current = 1;
      trackedRef.current = new Set();
      setIdx(0);
      setResolvedIdx(null);
      setOpen(true);
      try { track('tour_started'); } catch (_) { /* fail-silent */ }
    };
    start();

    const onRestart = () => {
      if (!productTourEnabled()) return;
      if (isMobileViewport()) return;
      dirRef.current = 1;
      trackedRef.current = new Set();
      setIdx(0);
      setResolvedIdx(null);
      setOpen(true);
      try { track('tour_started'); } catch (_) { /* fail-silent */ }
    };
    window.addEventListener('dathent:tour:restart', onRestart);
    return () => {
      cancelled = true;
      window.removeEventListener('dathent:tour:restart', onRestart);
    };
  }, [user]);

  const finish = (reason, atStep) => {
    setOpen(false);
    setResolvedIdx(null);
    setRect(null);
    writeDone(user?.id);
    try {
      if (reason === 'skipped') track('tour_skipped', { at_step: atStep });
      else track('tour_completed');
    } catch (_) { /* fail-silent */ }
  };

  // ---- Resolution: find the current idx's element, skipping silently over
  // steps whose target isn't on the page (one short retry for late-mounting
  // widgets, mirroring the existing DashboardTour's pattern) ----
  useEffect(() => {
    if (!open) return undefined;
    if (idx >= TOUR_STEPS.length) { finish('completed'); return undefined; }
    if (idx < 0) { setIdx(0); return undefined; }

    let cancelled = false;
    let timer = null;
    let attempts = 0;

    const check = () => {
      if (cancelled) return;
      const step = TOUR_STEPS[idx];
      const el = step?.selector ? document.querySelector(step.selector) : null;
      if (el) { setResolvedIdx(idx); return; }
      attempts += 1;
      if (attempts <= 1) { timer = window.setTimeout(check, 300); return; }
      const nextIdx = idx + dirRef.current;
      if (nextIdx < 0) { setResolvedIdx(null); return; } // nothing earlier to show — stay hidden
      if (nextIdx >= TOUR_STEPS.length) { finish('completed'); return; }
      setIdx(nextIdx);
    };

    setResolvedIdx(null);
    check();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx]);

  // ---- Live position + suppression poll for the resolved step ----
  useEffect(() => {
    if (!open || resolvedIdx == null) return undefined;
    const step = TOUR_STEPS[resolvedIdx];
    if (!step) return undefined;
    let raf = null;

    const update = () => {
      if (isSuppressed()) { setRect(null); return; }
      const el = document.querySelector(step.selector);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      if (r.top < 90 || r.top > window.innerHeight - 160) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { /* noop */ }
      }
      if (!trackedRef.current.has(step.id)) {
        trackedRef.current.add(step.id);
        try { track('tour_step', { id: step.id }); } catch (_) { /* fail-silent */ }
      }
    };

    update();
    const onResize = () => { raf = requestAnimationFrame(update); };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, { passive: true });
    const interval = window.setInterval(update, 400);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize);
      window.clearInterval(interval);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resolvedIdx]);

  if (!open || resolvedIdx == null || !rect) return null;

  const step = TOUR_STEPS[resolvedIdx];
  const total = TOUR_STEPS.length;
  const current = resolvedIdx + 1;
  const isLast = resolvedIdx === TOUR_STEPS.length - 1;

  const goNext = () => { dirRef.current = 1; setIdx((i) => i + 1); };
  const goBack = () => { dirRef.current = -1; setIdx((i) => Math.max(0, i - 1)); };
  const skip = () => finish('skipped', resolvedIdx);

  // Placement: below the target when there's room, otherwise above. Left is
  // clamped so the card never runs off either edge.
  const pad = 10;
  const cardW = 300;
  const placeBelow = rect.top < window.innerHeight - 260;
  const left = Math.max(16, Math.min(window.innerWidth - cardW - 16, rect.left));
  const arrowSide = placeBelow ? 'top' : 'bottom';
  const cardStyle = placeBelow
    ? { top: rect.top + rect.height + pad, left, width: cardW }
    : { bottom: Math.max(16, window.innerHeight - rect.top + pad), left, width: cardW };

  return (
    <>
      <div
        className="pt-highlight"
        style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }}
        aria-hidden="true"
      />
      <div
        className={`pt-tooltip pt-arrow-${arrowSide}`}
        style={cardStyle}
        role="dialog"
        aria-modal="false"
        aria-label={t(step.titleKey)}
      >
        <div className="pt-tooltip__head">
          <span className="pt-tooltip__progress">
            {t('productTour.progress', { current, total })}
          </span>
        </div>
        <h4 className="pt-tooltip__title">{t(step.titleKey)}</h4>
        <p className="pt-tooltip__body">{t(step.bodyKey)}</p>
        <div className="pt-tooltip__foot">
          <button type="button" className="pt-tooltip__skip" onClick={skip}>
            {t('productTour.skip')}
          </button>
          <div className="pt-tooltip__nav">
            {resolvedIdx > 0 && (
              <button type="button" className="pt-btn pt-btn-ghost" onClick={goBack}>
                {t('productTour.back')}
              </button>
            )}
            <button type="button" className="pt-btn pt-btn-primary" onClick={goNext}>
              {isLast ? t('productTour.done') : t('productTour.next')}
              {!isLast && <Icon name="chev_r" size={12} />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
